import React, { ReactNode, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom-v5';
import _ from 'lodash';
import { CircularProgress } from '@mui/material';
import styled from 'styled-components';
import { actions } from 'spw-redux/modules/projects/actions';
import * as Sentry from '@sentry/react';
import {
  ID_TYPE,
  ID_TYPE_URL_PARAM,
  setSummarySelectedIdType,
} from 'spw-redux/modules/program-summary/actions';
import * as ouActions from 'spw-redux/modules/ou/actions';
import { selectCurrentScenario } from 'spw-redux/modules/calculators/selectors';
import { selectBuildingDetailsView } from 'spw-redux/modules/building-detail/selectors';
import { IAppState } from '../../redux/reducer';
import { IBuilding, IFloor } from '../../redux/modules/projects/types';
import {
  BUILDING_VIEWS,
  EXISTING_SITE_ANALYSIS,
  REQUEST_STATUS,
} from '../../constants';
import { clearProjectDetailsSearchParams } from '../../shared/project-detials-utils';

const isProjectDetailsRoute = (pathname: string) =>
  pathname.includes('/projects');

const getProjectIdFromPathname = (pathname: string) => {
  if (!isProjectDetailsRoute(pathname)) {
    return null;
  }
  return pathname.split('/projects/')[1]?.split('/')[1];
};

const isProjectProfileRoute = (pathname: string) =>
  !!pathname.match(/^\/projects\/\d+$/);

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const RouterActionListener: React.FC<{ children: ReactNode | ReactNode[] }> = ({
  children,
}) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [showSpinner, setShowSpinner] = useState<boolean>(true);
  const [prevProjectId, setPrevProjectId] = useState<string>();
  const [loadingProject, setLoadingProject] = useState<boolean>(false);

  const buildingDetailsView = useSelector(selectBuildingDetailsView);

  const currentSelectedType = useSelector(
    (state: IAppState) => state.programSummaryReducer.selectedType,
    shallowEqual
  );

  const currentProject = useSelector(
    (state: IAppState) => state.projectsReducer.currentProject,
    shallowEqual
  );

  const currentBuilding = useSelector(
    (state: IAppState) => state.projectsReducer.currentBuilding,
    shallowEqual
  );

  const currentFloor = useSelector(
    (state: IAppState) => state.projectsReducer.currentFloor,
    shallowEqual
  );

  const rounds = useSelector(
    (state: IAppState) => state.ouReducer.rounds,
    shallowEqual
  );

  const currentRound = useSelector(
    (state: IAppState) => state.ouReducer.currentRound,
    shallowEqual
  );

  const currentRoundBuilding = useSelector(
    (state: IAppState) => state.ouReducer.currentRoundBuilding,
    shallowEqual
  );

  const currentRoundFloor = useSelector(
    (state: IAppState) => state.ouReducer.currentRoundFloor,
    shallowEqual
  );

  const currentScenario = useSelector(selectCurrentScenario, shallowEqual);

  const roundsRequestStatus = useSelector(
    (state: IAppState) => state.ouReducer.roundsRequestStatus
  );

  useEffect(() => {
    if (history.location.pathname !== location.pathname) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);

    if (!isProjectDetailsRoute(location.pathname)) {
      clearProjectDetailsSearchParams(searchParams);
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
      return;
    }

    if (showSpinner) {
      return;
    }

    const projectId = getProjectIdFromPathname(location.pathname);

    if (prevProjectId && projectId !== prevProjectId) {
      clearProjectDetailsSearchParams(searchParams);
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
      setPrevProjectId(projectId);
      return;
    }

    let changed = false;
    const searchCurrentView = searchParams.get('current_view');
    const buildingId = parseInt(searchParams.get('building'), 10);
    const searchCurrentFloor = +searchParams.get('floor');
    const searchCurrentRound = +searchParams.get('round');
    const searchCurrentRoundBuilding = +searchParams.get('round_building');
    const searchCurrentRoundFloor = +searchParams.get('round_floor');
    const searchCurrentSelectedType = _.invert(ID_TYPE_URL_PARAM)[
      searchParams.get('selected_type')
    ];

    if (
      !location.pathname.includes('/building') &&
      searchCurrentView === BUILDING_VIEWS.OCCUPANCY &&
      currentProject?.type === EXISTING_SITE_ANALYSIS
    ) {
      searchParams.delete('current_view');
      changed = true;
    } else if (
      location.pathname.includes('/building') &&
      buildingDetailsView &&
      searchCurrentView !== buildingDetailsView &&
      currentProject?.type === EXISTING_SITE_ANALYSIS
    ) {
      searchParams.set('current_view', buildingDetailsView);
      changed = true;
    }

    if (
      searchCurrentSelectedType &&
      !currentSelectedType &&
      currentProject?.type === EXISTING_SITE_ANALYSIS
    ) {
      searchParams.delete('selected_type');
      changed = true;
    } else if (
      currentSelectedType &&
      searchCurrentSelectedType !== currentSelectedType &&
      !isProjectProfileRoute(location.pathname) &&
      currentProject?.type === EXISTING_SITE_ANALYSIS
    ) {
      searchParams.set('selected_type', ID_TYPE_URL_PARAM[currentSelectedType]);
      changed = true;
    }

    if (searchCurrentFloor !== currentFloor?.id) {
      if (!currentFloor?.id) {
        searchParams.delete('floor');
        changed = true;
      } else if (!isProjectProfileRoute(location.pathname)) {
        searchParams.set('floor', `${currentFloor?.id}`);
        if (searchParams.get('building')) searchParams.delete('building');
        changed = true;
      }
    }

    if (
      buildingId !== currentBuilding?.id &&
      !searchParams.get('floor') &&
      currentProject?.type === EXISTING_SITE_ANALYSIS
    ) {
      if (!currentBuilding?.id) {
        searchParams.delete('building');
      } else if (!isProjectProfileRoute(location.pathname)) {
        searchParams.set('building', `${currentBuilding?.id}`);
      }
      changed = true;
    }

    if (
      searchParams.get('selected_type') !== ID_TYPE_URL_PARAM.floorId &&
      searchParams.get('floor')
    ) {
      searchParams.delete('floor');
      changed = true;
    }

    if (searchCurrentRound !== currentRound?.id) {
      if (!currentRound?.id) {
        searchParams.delete('round');
      } else {
        searchParams.set('round', `${currentRound?.id}`);
      }
      changed = true;
    }

    if (searchCurrentRoundBuilding !== currentRoundBuilding?.id) {
      if (!currentRoundBuilding?.id) {
        searchParams.delete('round_building');
      } else {
        searchParams.set('round_building', `${currentRoundBuilding?.id}`);
      }
      changed = true;
    }

    if (searchCurrentRoundFloor !== currentRoundFloor?.id) {
      if (!currentRoundFloor?.id) {
        searchParams.delete('round_floor');
      } else {
        searchParams.set('round_floor', `${currentRoundFloor?.id}`);
      }
      changed = true;
    }

    if (changed) {
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }
  }, [
    history,
    location.pathname,
    location.search,
    currentProject?.type,
    prevProjectId,
    buildingDetailsView,
    currentFloor,
    currentBuilding,
    currentScenario,
    currentSelectedType,
    currentRound,
    currentRoundBuilding,
    currentRoundFloor,
    showSpinner,
  ]);

  useEffect(() => {
    if (showSpinner) {
      if (!isProjectDetailsRoute(location.pathname)) {
        setShowSpinner(false);
        return;
      }

      const projectId = getProjectIdFromPathname(location.pathname);

      if (!projectId) {
        setShowSpinner(false);
        return;
      }

      // If there isn't a current project, get the project info
      if (!currentProject && !loadingProject) {
        dispatch(actions.getProjectInfoById(Number(projectId)));
        setLoadingProject(true);
        return;
      }

      // Return if the project is still loading
      if (loadingProject && !currentProject) {
        return;
      }

      const searchParams = new URLSearchParams(location.search);
      const searchCurrentSelectedType = _.invert(ID_TYPE_URL_PARAM)[
        searchParams.get('selected_type')
      ];
      const searchCurrentBuilding = +searchParams.get('building');
      const searchCurrentFloor = +searchParams.get('floor');
      const roundId = parseInt(searchParams.get('round'), 10);

      if (
        searchCurrentSelectedType &&
        currentProject?.type === EXISTING_SITE_ANALYSIS
      ) {
        if (searchCurrentSelectedType === ID_TYPE.buildingId) {
          dispatch(
            setSummarySelectedIdType(searchCurrentBuilding, ID_TYPE.buildingId)
          );
        } else if (searchCurrentSelectedType === ID_TYPE.floorId) {
          dispatch(
            setSummarySelectedIdType(searchCurrentFloor, ID_TYPE.floorId)
          );
        } else {
          dispatch(setSummarySelectedIdType(null, searchCurrentSelectedType));
        }
      }

      if (
        searchCurrentBuilding &&
        currentProject?.type === EXISTING_SITE_ANALYSIS
      ) {
        const building = currentProject?.buildings?.find(
          (b) => b.id === +searchCurrentBuilding
        );

        if (building) {
          dispatch(actions.setCurrentBuilding(building));
        }
      }

      if (searchCurrentFloor) {
        const floors = currentProject?.buildings.reduce(
          (
            acc,
            building: IBuilding
          ): (IFloor & {
            building: IBuilding;
          })[] => [
            ...acc,
            ...building.floors.map((floor: IFloor) => ({ ...floor, building })),
          ],
          []
        );

        const floor = floors?.find((f) => f.id === +searchCurrentFloor);

        if (!currentBuilding && floor) {
          dispatch(actions.setCurrentBuilding(floor.building));
        }

        if (floor) {
          dispatch(
            actions.setCurrentFloor(_.omit(floor, 'building') as IFloor)
          );
        }
      }

      // Get rounds if the round param is present in the URL
      if (roundId && currentProject.type === EXISTING_SITE_ANALYSIS) {
        if (!rounds.length && roundsRequestStatus === REQUEST_STATUS.NONE) {
          dispatch(ouActions.actions.fetchProjectsRounds(Number(projectId)));
          return;
        }

        if (roundsRequestStatus === REQUEST_STATUS.QUERYING) {
          return;
        }

        if (rounds.length && roundsRequestStatus === REQUEST_STATUS.SUCCESS) {
          const round = rounds?.find((r) => r.id === roundId);
          if (round) {
            dispatch(ouActions.actions.setCurrentRound(round));
          }
        }
      }

      setShowSpinner(false);
    }
  }, [
    location.pathname,
    location.search,
    currentProject,
    currentBuilding,
    rounds,
    roundsRequestStatus,
    loadingProject,
    showSpinner,
    dispatch,
  ]);

  if (showSpinner) {
    return (
      <Sentry.ErrorBoundary
        key={history.location.pathname}
        beforeCapture={(scope) => {
          // If there is an error, it will be sent to Sentry and the spinner will disappear
          setShowSpinner(false);
          scope.setTag('location', history.location.pathname);
        }}
      >
        <LoadingWrapper>
          <CircularProgress />
        </LoadingWrapper>
      </Sentry.ErrorBoundary>
    );
  }

  return <>{children}</>;
};

export default RouterActionListener;
